'use client'

import { LDClient, initialize } from 'launchdarkly-js-client-sdk'
import { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog'
import {
  createLaunchDarklyClientConfig,
  createLaunchDarklyContext,
} from '../_config/Experimentation.config'
import { useNextQuery } from '../_hooks/useNextQuery'

import { useAuthentication } from './AuthenticationProvider.client'
import { useSession } from './SessionProvider.client'
import { useTrackFeatureFlagDecision } from './TrackingProvider.client'

const createClient = ({
  context,
  initialState,
}: {
  context: ReturnType<typeof createLaunchDarklyContext>
  initialState: Record<PropertyKey, unknown> | undefined
}) => {
  if (typeof window === 'undefined') return undefined
  if (!process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_API_KEY) return undefined

  const config = createLaunchDarklyClientConfig({ bootstrap: initialState })
  const client = initialize(process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_API_KEY, context, config)

  return client
}

const useClientImpl = (initialState: Record<PropertyKey, unknown> | undefined) => {
  const { account } = useAuthentication()
  const { attributes } = useSession()
  const [_, startTransition] = useTransition()

  const { data: portalData } = useNextQuery(
    gql(`
      query GetPortalInfoForLaunchDarklyContext {
        my {
          id
          portal {
            id
            name
          }
        }
      }
    `),
    {
      client: 'auth-supported',
      skip: !account,
    }
  )

  const context = useMemo(() => {
    return createLaunchDarklyContext({
      account,
      sessionAttributes: attributes,
      portal: portalData?.my.portal
        ? {
            id: portalData.my.portal.id,
            name: portalData.my.portal.name,
          }
        : undefined,
    })
  }, [account, attributes, portalData?.my.portal])
  const lastContextUsedRef = useRef(context)

  const clientRef = useRef<LDClient | undefined>(undefined)

  if (!clientRef.current) {
    clientRef.current = createClient({
      context,
      initialState,
    })
    lastContextUsedRef.current = context
  }

  if (clientRef.current && context !== lastContextUsedRef.current) {
    clientRef.current.identify(context)
    lastContextUsedRef.current = context
  }

  const [isClientReady, setIsClientReady] = useState(() => {
    const startedWithAClient = !!clientRef.current
    const startedWithFlagDecisions = !!initialState
    return startedWithAClient && startedWithFlagDecisions
  })

  const internalsRef = useRef<{
    disabledFeatures: Record<string, true>
    synchronousFlagsDisabled: boolean
  }>({
    disabledFeatures: {},
    synchronousFlagsDisabled: !isClientReady,
  })

  useEffect(() => {
    if (isClientReady) return

    clientRef.current
      ?.waitUntilReady()
      .then(() => {
        // I know this is sketchy... but it shouldn't cause any bugs or issues.
        // We don't have a precise way to know the entire page has gone through its initial hydration/render.
        // In the page's directory, we need the initial renders to run and check their flags prior to the client being "ready".
        // The client is not primed with `initialState` due to these pages being cached/shared despite `initialState` being user specific.
        // Without this, if the client gets ready too quick, we can have CLS in pages directory due to SSR being the control flag and clientside being the user's evaluation.
        // Once we are fully off of cached HTML, we can completely rip out everything related to `ifAccessedPriorToDecisionInitialization` and `isClientReady` and just require `initialState` always.

        // Wait a time amount that is similar to how long it took content to appear.
        setTimeout(() => {
          // Deprioritize this React update.
          startTransition(() => {
            // Defer until browser is ready for a visual update.
            requestAnimationFrame(() => {
              // Now assume we have done an initial render for page sections above the fold.
              setIsClientReady(true)
            })
          })
        }, 1500)
      })
      .catch(() => {})
  }, [isClientReady])

  if (isClientReady) {
    internalsRef.current.synchronousFlagsDisabled = false
  }

  return {
    client: isClientReady ? clientRef.current : undefined,
    initialFlags: initialState,
    _internals: internalsRef.current,
  }
}

const ExperimentationContainer = createContainer(useClientImpl)
ExperimentationContainer.Provider.displayName = 'ExperimentationProviderImpl'
export const ExperimentationProvider = ExperimentationContainer.Provider

export function useFeatureFlag<T extends string>(options: {
  key: string
  controlVariant: T
  ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant'
  skip?: boolean
}): T

export function useFeatureFlag<T extends string>(options: {
  key: string
  controlVariant: T
  ifAccessedPriorToDecisionInitialization: 'return-null-while-pending'
  skip?: boolean
}): null | T

export function useFeatureFlag<T extends string>({
  key,
  controlVariant,
  ifAccessedPriorToDecisionInitialization,
  skip,
}: {
  key: string
  controlVariant: T
  ifAccessedPriorToDecisionInitialization:
    | 'return-null-while-pending'
    | 'lock-pageview-to-controlVariant'
  skip?: boolean
}) {
  const { client, initialFlags, _internals } = ExperimentationContainer.useContainer()
  const { trackFeatureFlagDecision } = useTrackFeatureFlagDecision()

  if (skip === true) {
    return controlVariant
  }

  if (
    _internals.synchronousFlagsDisabled &&
    ifAccessedPriorToDecisionInitialization === 'lock-pageview-to-controlVariant'
  ) {
    _internals.disabledFeatures[key] = true
  }

  if (_internals.disabledFeatures[key]) {
    return controlVariant
  }

  if (!client && !initialFlags) {
    if (ifAccessedPriorToDecisionInitialization === 'lock-pageview-to-controlVariant') {
      return controlVariant
    } else {
      return null
    }
  }

  const decision = client
    ? `${client.variation(key, controlVariant)}`
    : initialFlags?.[key]
    ? `${initialFlags[key]}`
    : controlVariant

  trackFeatureFlagDecision({
    key,
    decision,
  })

  return decision
}
